import { useEffect, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useStore } from '../core/store'
import { centerView, visibleField, type Field } from '../core/framing'
import type { Bounds } from '../core/layout'
import type { Viewport } from '../core/types'
import { useGraphFrame } from '../canvas/useGraphFrame'
import { OP_LABEL, channelOf, type OpKind } from '../core/types'
import { abortInFlight, cancelCurrent } from '../engine/reason'
import { readPalette, rgba } from '../core/palette'
import { motion } from '../core/motion'
import { drawnRatingsOf } from '../core/rewrite'
import { onAfterDraw } from '../canvas/frameBus'
import { screen } from '../canvas/screen'
import { liveOpOf } from '../core/passTime'
import { EXECUTABLE_LABEL, isExecutableTest } from '../core/narrationReceipt'
import { PassClock, UsualNote, useUsual } from './LiveWait'

/* ── The model's reasoning, live ──────────────────────────────────────── */

/**
 * Summarised thinking, streamed straight from the model.
 *
 * This is not a progress animation dressed up as insight — it is the actual
 * reasoning trace, which is why it is worth the screen space. When the model
 * says "checking whether the bound tightens or loosens", it is doing that.
 */
/**
 * What each pass is called while it runs.
 *
 * A `Record<OpKind | 'seed', string>` rather than a switch with a default. The
 * switch's `default: 'Thinking'` was a silent-failure surface: a new op kind got
 * the generic label with nothing to say it had been missed — and it would have said
 * "Thinking" over the most expensive pass in the app. As a Record, a missing entry
 * is a compile error.
 */
const THOUGHT_LABEL: Record<OpKind | 'seed', string> = {
  expand: 'Opening several directions',
  challenge: 'Looking for what breaks it',
  deepen: 'Decomposing the mechanism',
  diverge: 'Reframing from elsewhere',
  experiment: 'Designing a falsification',
  invert: 'Assuming the opposite',
  bridge: 'Searching for a synthesis',
  /** Names the attempt, not a result. This pass is looking for the work that
   *  joins two claims, and most of what it reports is that it did not find any. */
  link: 'Searching for work joining both',
  revise: 'Re-deriving from your revision',
  verify: 'Searching for what the record says',
  panel: 'Asking three lenses separately',
  audit: 'Another provider is attacking this branch',
  outcome: 'Re-deriving from what you observed',
  rebase: 'Rethinking after a changed check',
  /** The attempt, stated as one. Never "finding something new": whether any of
   *  it is new is a search's to report, and most proposals will not survive it. */
  conjecture: 'Proposing what no source it knows has said',
  /** The attempt, as the link scan's line names its own: going after the work
   *  that already says this. Most of what it can report is that it did not. */
  priorArt: 'Searching for work that already says this',
  /** Where the field is unsettled — the pages' word for it, not this app's. */
  sweep: 'Searching for where the field is unsettled',
  research: 'Structuring an auditable research record',
  survey: 'Searching for what the field has found',
  /** Names a proposal: nothing this pass says lands until a person approves it. */
  plan: 'Proposing a list of moves for you to approve',
  /** Names the act on the person's own words, not a judgement of them. */
  sharpen: 'Sharpening what you wrote',
  portfolio: 'Working several compatible angles in one pass',
  seed: 'Thinking',
}

/** A conjecture's executable test while it runs. Filed as a `research` op (D283),
 *  it wore the Lab's line above: a test being written, not a record structured. */
export const EXECUTABLE_THOUGHT = 'Writing an executable test of the prediction'

/**
 * The live line's name for the pass and its gerund phrase, with an executable
 * test named for what it is (`isExecutableTest`, D289) rather than for the kind it
 * is filed under.
 */
export function thoughtNames(
  kind: OpKind | 'seed',
  executable: boolean,
): { name: string; doing: string } {
  if (executable) return { name: EXECUTABLE_LABEL, doing: EXECUTABLE_THOUGHT }
  return { name: kind === 'seed' ? 'Seed' : OP_LABEL[kind], doing: THOUGHT_LABEL[kind] }
}

/**
 * Each pass's tint on the live line. The six verbs match the node card's OPS
 * tints so a move is one colour everywhere; the paid passes take the channel
 * their product lands in. A total Record, not a lookup with a default — a new
 * op kind must choose its colour before it can ride the line.
 */
export const THOUGHT_TINT: Record<OpKind | 'seed', string> = {
  expand: 'var(--accent)',
  deepen: 'var(--sem-signal)',
  challenge: 'var(--sem-tension)',
  diverge: 'var(--sem-gap)',
  experiment: 'var(--sem-insight)',
  invert: 'var(--sem-weak)',
  bridge: 'var(--sem-insight)',
  link: 'var(--sem-gap)',
  revise: 'var(--sem-insight)',
  verify: 'var(--sem-signal)',
  panel: 'var(--sem-gap)',
  audit: 'var(--sem-tension)',
  outcome: 'var(--sem-insight)',
  rebase: 'var(--sem-gap)',
  // The channel its product lands in, as every paid pass here takes.
  conjecture: 'var(--sem-insight)',
  // The link scan's: both go looking for work that already joins or says a thing.
  priorArt: 'var(--sem-gap)',
  // An open question is the gap channel's own meaning.
  sweep: 'var(--sem-gap)',
  research: 'var(--accent)',
  survey: 'var(--sem-signal)',
  plan: 'var(--accent)',
  sharpen: 'var(--accent)',
  portfolio: 'var(--accent)',
  seed: 'var(--sem-signal)',
}

/**
 * Neither copy is a live region. The line's words are the model's reasoning as
 * it streams, rewritten on every delta, and a polite region over them queued an
 * announcement per delta — thirty-three for one sentence of a run's move, in
 * this build's own replay, each the whole growing sentence again. What a reader needs
 * from a pass is that it started and that it landed, which the page's announcer
 * says from the record (`ui/Announcer.tsx`), and a run's move, which the run
 * strip speaks. The reasoning itself is read where it is kept: the thread.
 *
 * `inFeed` is the same line in the run feed, where it is not an overlay but a
 * ROW — the one that has not landed yet, sitting exactly where the next move
 * will appear. It is hidden from the tree there, as a second copy of the one on
 * the canvas.
 *
 * And it wraps instead of clipping. Over the field this line is one clipped row
 * because it is competing with the graph for the eye. In the column the thinking
 * IS what the reader came for, and an ellipsis in the middle of it there would
 * hide the half that says what the model is actually doing.
 */
export function Thoughtstream({ inFeed = false }: { inFeed?: boolean }) {
  const busy = useStore((s) => s.busy)
  const thought = useStore((s) => s.thought)
  const searching = useStore((s) => s.searching)
  const waiting = useStore((s) => s.waiting)
  const activeOp = useStore((s) => s.activeOp)
  const inRun = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  // A pass that is BUSY while the run is WAITING at its confirm gate is the
  // person's own hand-fired verb — the run has nothing in flight at a pause —
  // so it gets its Stop back, and that Stop aborts the pass alone: the only
  // other stop on screen would end the run they were steering.
  const runWaiting = useStore((s) => s.run?.phase === 'waiting')
  // The pass in flight, for its clock and its usual figure. The op object itself:
  // it keeps its identity while it runs unless its nodes are written onto it.
  const op = useStore((s) => liveOpOf(s.session, s.busy))
  const usual = useUsual(op)
  // A boolean, so the line re-renders when the answer changes and not on every
  // write the live pass makes to the session.
  const executable = useStore((s) => {
    const live = liveOpOf(s.session, s.busy)
    return live !== null && isExecutableTest(s.session, live)
  })

  if (!busy && !thought) return null

  const kind: OpKind | 'seed' = activeOp ?? 'seed'
  const tint = THOUGHT_TINT[kind]
  const names = thoughtNames(kind, executable)

  // The tail is the TICKER'S OWN take, cut here rather than at the source: the
  // store now carries the whole trace, because the feed streams it as the
  // pass's message, while this line still wants "the current line of
  // reasoning, not a transcript". The threshold spares the two passes that
  // pin a sentence to the FRONT of a pre-cut tail — the panel's seat line and
  // the audit's — whose strings arrive at ~620 characters; slicing those again
  // would eat the pin's opening words.
  const tail = thought.length > 700 ? thought.slice(-600) : thought
  // After a thought, a wait to ask again takes the search line's place.
  const line = tail && waiting ? waiting : searching

  return (
    <div
      className={inFeed ? 'thoughtstream thoughtstream--feed' : 'thoughtstream'}
      data-on={busy || !!thought}
      aria-hidden={inFeed || undefined}
    >
      <i
        className="thoughtstream-dot"
        data-still={!busy || undefined}
        style={{ background: tint, boxShadow: `0 0 9px ${tint}` }}
      />
      {/* The move's short name; the gerund phrase survives as the title. */}
      <span className="thoughtstream-kind" style={{ color: tint }} title={names.doing}>
        {names.name}
      </span>
      <span className="thoughtstream-text">
        {/* A wait to ask again stands where the placeholder would, before any
            thought; after, it takes the search line (below). */}
        {tail || waiting || 'Reading the branch from the root down…'}
        {busy && <i className="caret" />}
      </span>
      {/* How long it has been going, beside the Stop that would end it. */}
      {op && <PassClock since={op.startedAt} className="thoughtstream-clock" />}
      {/* Stop rides the line only for a lone pass. During a run the run strip owns
          stopping — two stop affordances for one run would race each other.
          `!inFeed` is redundant today and stated anyway: the feed copy renders
          only inside a running run's card, so `inRun` already covers it — but
          that is a guarantee held in another component, and the cost of it ever
          slipping is a focusable button inside an `aria-hidden` subtree, which
          is a trap for exactly the reader the attribute is there to serve. */}
      {busy && (!inRun || runWaiting) && !inFeed && (
        <button
          type="button"
          className="thoughtstream-stop"
          aria-label="Stop this pass"
          title="Stop this pass — what has already arrived is kept, and it was billed either way"
          onClick={() => (inRun ? abortInFlight() : cancelCurrent())}
        >
          Stop
        </button>
      )}
      {/* A second line rather than part of the trace. The trace is the model's
          reasoning; this is an action it took, and splicing one into the other
          would make the retained trace claim to contain something the model
          never wrote. The newest step only: this is the one status line around
          the input, and the steps before it are listed in the live pass's own
          message. "Usually" rides the same row, at its end — on a phone the
          first row has no room for it beside the trace. */}
      {(line || (op && usual)) && (
        <span className="thoughtstream-foot">
          {line && <span className="thoughtstream-search">{line}</span>}
          {op && usual && <UsualNote usual={usual} op={op} />}
        </span>
      )}
    </div>
  )
}

/* The legend lives in the tool rail's eye popover — see ui/CanvasChrome.tsx. */

/* The replay transport and the scrubber live in the graph footer — see
   `GraphFooter` in ui/Ledger.tsx (D369). The pace select lives in Settings,
   because it also governs LIVE growth and replay is only half of what it paces. */

/* ── Minimap ──────────────────────────────────────────────────────────── */

export function Minimap({ stageW, stageH }: { stageW: number; stageH: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const session = useStore((s) => s.session)
  const rev = useStore((s) => s.rev)
  const bounds = useStore((s) => s.bounds)
  const theme = useStore((s) => s.theme)
  // It used to draw every node in `order` with no visibility filter at all —
  // already wrong for the channel filters and the timeline, and plainly wrong
  // once a branch can be folded away.
  const { visible, folded } = useGraphFrame()

  useEffect(() => {
    // On the canvas's own clock rather than a loop of its own, and only on the
    // frames where something it draws moved: the renderer bumps `screen.frame`
    // when the camera, the eased layout or the revision changes, and the view
    // rectangle follows `view`. Everything else the minimap shows is in this
    // effect's dependencies, and a fresh subscription paints once regardless.
    // It used to repaint every node sixty times a second for a picture that
    // changed a few times a minute (docs/performance-audit.md, finding 6).
    let lastFrame = -1
    let lastView: unknown = null
    const draw = () => {
      const c = canvasRef.current
      if (!c) return
      const view = useStore.getState().view
      if (screen.frame === lastFrame && view === lastView) return
      lastFrame = screen.frame
      lastView = view
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = c.clientWidth
      const h = c.clientHeight
      if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
        c.width = Math.round(w * dpr)
        c.height = Math.round(h * dpr)
      }
      const ctx = c.getContext('2d')
      if (!ctx) return
      const p = readPaletteCached(theme)

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = rgba(p.canvasEdge, 1)
      ctx.fillRect(0, 0, w, h)

      const bw = Math.max(1, bounds.maxX - bounds.minX)
      const bh = Math.max(1, bounds.maxY - bounds.minY)
      const k = Math.min(w / bw, h / bh) * 0.9
      const ox = w / 2 - ((bounds.minX + bounds.maxX) / 2) * k
      const oy = h / 2 - ((bounds.minY + bounds.maxY) / 2) * k

      for (const id of session.order) {
        const n = session.nodes[id]
        if (!n) continue
        if (!visible.has(id)) continue
        const ch = channelOf(n)
        const col = p.channel[ch]
        // The minimap's dead dot rides the same raise as the canvas's: at 0.3 it
        // was a smudge in a view whose whole job is "where am I", and a spent
        // branch you cannot locate there is a branch you cannot navigate back to.
        ctx.fillStyle = rgba(col, n.parentId === null ? 0.95 : ch === 'dead' ? 0.46 : 0.8)
        const f = folded.get(id) ?? 0
        const r =
          n.parentId === null
            ? 3
            : f > 0
              ? 2.2 + Math.min(2.4, Math.log2(1 + f) * 0.5)
              : 1.2 + drawnRatingsOf(n).novelty * 1.2
        const at = motion.at(n)
        ctx.beginPath()
        ctx.arc(ox + at.x * k, oy + at.y * k, r, 0, Math.PI * 2)
        ctx.fill()
      }

      // The viewport rectangle, in graph space.
      const v = view
      const x0 = (-v.x / v.k) * k + ox
      const y0 = (-v.y / v.k) * k + oy
      ctx.strokeStyle = rgba(p.text1, 0.34)
      ctx.lineWidth = 1
      ctx.strokeRect(x0, y0, (stageW / v.k) * k, (stageH / v.k) * k)
    }
    draw()
    return onAfterDraw(draw)
  }, [session, rev, bounds, theme, stageW, stageH, visible, folded])

  // A press, and a drag after it, both move the view: a minimap's viewport is a
  // thing a hand drags, and one that answered only the first press took a run of
  // presses to cross a large graph (D335). Each is the reader's own move, so it
  // pins the view (`setView`'s default) as a pan does.
  const pick = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const st = useStore.getState()
    st.setView(
      minimapView(
        { x: e.clientX - r.left, y: e.clientY - r.top },
        { w: r.width, h: r.height },
        bounds,
        st.view.k,
        visibleField(stageW, stageH, st.viewInset),
      ),
    )
  }

  return (
    <div className="minimap panel" aria-hidden="true">
      <canvas
        ref={canvasRef}
        onPointerDown={(e) => {
          if (e.button !== 0) return
          e.currentTarget.setPointerCapture(e.pointerId)
          pick(e)
        }}
        onPointerMove={(e) => {
          if (e.buttons & 1 && e.currentTarget.hasPointerCapture(e.pointerId)) pick(e)
        }}
      />
    </div>
  )
}

/**
 * The view a press on the minimap asks for: the picked world point at the
 * centre of the field the reader can SEE, at the zoom they are at.
 *
 * The visible field, not the stage. The stage runs under the floating panes, so
 * centring on the stage's middle put the pick partly under the inspector's glass
 * — while the dock's zoom and Fit already worked in the visible field (D230,
 * D256), and two controls on one canvas disagreed about where its middle was.
 */
export function minimapView(
  at: { x: number; y: number },
  box: { w: number; h: number },
  bounds: Bounds,
  k: number,
  field: Field,
): Viewport {
  const bw = Math.max(1, bounds.maxX - bounds.minX)
  const bh = Math.max(1, bounds.maxY - bounds.minY)
  const s = Math.min(box.w / bw, box.h / bh) * 0.9
  const ox = box.w / 2 - ((bounds.minX + bounds.maxX) / 2) * s
  const oy = box.h / 2 - ((bounds.minY + bounds.maxY) / 2) * s
  return centerView({ x: (at.x - ox) / s, y: (at.y - oy) / s }, k, field)
}

let paletteCache: { theme: string; value: ReturnType<typeof readPalette> } | null = null
function readPaletteCached(theme: 'light' | 'dark') {
  if (paletteCache?.theme !== theme) paletteCache = { theme, value: readPalette(theme) }
  return paletteCache.value
}

/* The zoom cluster moved into the tool dock — see ui/CanvasChrome.tsx. */
